import { useId, type ReactNode } from 'react';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/cn';

/**
 * Label + control + hint/error, the shape every form row in the app already
 * used — twelve times, with twelve slightly different implementations. Some
 * showed errors, some didn't; some marked required fields, some didn't; the
 * gap between label and control varied per file.
 *
 * `error` replaces `hint` when present, since showing both puts the reason it
 * failed underneath advice about how to fill it in.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  /** Span both columns of a two-column form grid. */
  wide,
  className,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  wide?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const generatedId = useId();
  const describedBy = `${htmlFor ?? generatedId}-description`;

  return (
    <div className={cn('flex flex-col gap-1.5', wide && 'md:col-span-2', className)}>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      {children}
      {error ? (
        <p id={describedBy} role="alert" className="text-[11px] text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={describedBy} className="text-[11px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Settings-style row: description on the left, control on the right. Used where
 * a preference needs explaining more than it needs a form label.
 */
export function SettingRow({
  label,
  hint,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 rounded-md border border-border bg-surface px-3 py-2.5',
        className,
      )}
    >
      <div className="min-w-0">
        <Label htmlFor={htmlFor} className="text-sm font-medium">
          {label}
        </Label>
        {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}
